import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { href: "#story", key: "story" },
  { href: "#properties", key: "properties" },
  { href: "#location", key: "location" },
  { href: "#region", key: "region" },
];

export default function Navigation() {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#FAFAF7]/85 border-b border-[#E2DFD2]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-home" className="flex items-baseline gap-1">
          <span className="font-serif text-xl md:text-2xl tracking-tight text-[#112A46]">Villa d'</span>
          <span className="font-serif italic text-xl md:text-2xl text-[#C48E66]">Avó Dalva</span>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_ITEMS.map(({ href, key }) => (
            <a key={key} href={href} data-testid={`nav-${key}`}
              className="nav-link text-[11px] uppercase tracking-[0.25em] font-medium text-[#1C2321] hover:text-[#112A46]">
              {t.nav[key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <button
            onClick={toggle}
            data-testid="lang-toggle-btn"
            className="text-[11px] uppercase tracking-[0.25em] font-medium text-[#1C2321] hover:text-[#112A46] transition-colors"
            aria-label="Toggle language"
          >
            <span className={lang === "pt" ? "text-[#112A46]" : ""}>PT</span>
            <span className="mx-1 opacity-40">/</span>
            <span className={lang === "en" ? "text-[#112A46]" : ""}>EN</span>
          </button>
          <a
            href="#contact"
            data-testid="nav-cta-book"
            className="hidden md:inline-block text-[11px] uppercase tracking-[0.25em] font-medium border border-[#112A46] text-[#112A46] px-5 py-3 hover:bg-[#112A46] hover:text-[#FAFAF7] transition-all duration-300"
          >
            {t.nav.contact}
          </a>
          <button
            className="lg:hidden text-[#112A46]"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#FAFAF7] border-t border-[#E2DFD2]">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_ITEMS.map(({ href, key }) => (
              <a key={key} href={href} onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${key}`}
                className="text-sm uppercase tracking-[0.2em] text-[#1C2321]">
                {t.nav[key]}
              </a>
            ))}
            <a href="#contact" onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta"
              className="text-sm uppercase tracking-[0.2em] text-[#C48E66]">
              {t.nav.contact}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
